// ============================================================================
// KLINISOL CRO - App.js
// Added: ForgotPassword, ResetPassword, and Personnel routes
// ============================================================================

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';

// Pages
import Home from './pages/Home';
import About from './pages/About';
import Services from './pages/Services';
import TherapeuticAreas from './pages/TherapeuticAreas';
import Contact from './pages/Contact';
import ILOPlatform from './pages/ILOPlatform';
import ILOApp from './pages/ILOApp';
import Resources from './pages/Resources';
import ForInvestigators from './pages/ForInvestigators';
import NewsKiosk from './pages/NewsKiosk';
import TermsAndConditions from './pages/TermsAndConditions';
import Login from './components/Login';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Dashboard from './components/Dashboard';
import SOPLibrary from './pages/SOPLibrary';
import TrainingPortal from './pages/TrainingPortal';
import TrainingRecords from './pages/TrainingRecords';
import SOPUpload from './pages/SOPUpload';
import TrainingViewer from './pages/TrainingViewer';
import UserManagement from './pages/UserManagement';
import TrainingMatrixDashboard from './pages/TrainingMatrixDashboard';
import CertificateVerification from './pages/CertificateVerification';
import MasterSOPRegister from './pages/MasterSOPRegister';
import FormsLibrary from './pages/FormsLibrary';
import FormPortal from './components/FormPortal';
import FormRenderer from './components/FormRenderer';
import KnowledgeBaseManager from './pages/KnowledgeBaseManager';
import PersonnelDirectory from './pages/PersonnelDirectory';
import PersonnelProfile from './pages/PersonnelProfile';

// Components
import Navbar from './components/Navbar';
import QuizReview from './components/training/QuizReview';

// import './App.css';

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={
              <>
                <Navbar />
                <Home />
              </>
            } />
            
            <Route path="/about" element={
              <>
                <Navbar />
                <About />
              </>
            } />
            
            <Route path="/services" element={
              <>
                <Navbar />
                <Services />
              </>
            } />
            
            <Route path="/therapeutic-areas" element={
              <>
                <Navbar />
                <TherapeuticAreas />
              </>
            } />
            
            <Route path="/contact" element={
              <>
                <Navbar />
                <Contact />
              </>
            } />

            {/* ILO Platform Route (Public landing page) */}
            <Route path="/ilo-platform" element={
              <>
                <Navbar />
                <ILOPlatform />
              </>
            } />

            {/* Resources Routes */}
            <Route path="/resources" element={
              <>
                <Navbar />
                <Resources />
              </>
            } />

            <Route path="/for-investigators" element={
              <>
                <Navbar />
                <ForInvestigators />
              </>
            } />

            <Route path="/news-kiosk" element={
              <>
                <Navbar />
                <NewsKiosk />
              </>
            } />

            {/* Terms & Conditions Route */}
            <Route path="/terms-and-conditions" element={
              <>
                <Navbar />
                <TermsAndConditions />
              </>
            } />

            {/* Auth Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Certificate Verification Route (PUBLIC - no auth required for QR scanning) */}
            <Route path="/verify/certificate/:certificateNumber" element={<CertificateVerification />} />

            {/* Protected Routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />

            <Route path="/sop-library" element={
              <ProtectedRoute>
                <SOPLibrary />
              </ProtectedRoute>
            } />

            <Route path="/training-portal" element={
              <ProtectedRoute>
                <TrainingPortal />
              </ProtectedRoute>
            } />

            {/* Training Records / My Certificates */}
            <Route path="/training/records" element={
              <ProtectedRoute>
                <TrainingRecords />
              </ProtectedRoute>
            } />

            {/* Training Module Viewer Route */}
            <Route path="/training/module/:moduleId" element={
              <ProtectedRoute>
                <TrainingViewer />
              </ProtectedRoute>
            } />

            {/* Quiz Review Route */}
            <Route path="/training/quiz/review/:attemptId" element={
              <ProtectedRoute>
                <QuizReview />
              </ProtectedRoute>
            } />

            {/* SOP Upload Route (Super Admin Only) */}
            <Route path="/sop-upload" element={
              <ProtectedRoute>
                <SOPUpload />
              </ProtectedRoute>
            } />

            {/* Training Matrix Route */}
            <Route path="/training-matrix" element={
              <ProtectedRoute>
                <TrainingMatrixDashboard />
              </ProtectedRoute>
            } />

            {/* User Management Route (Super Admin Only) */}
            <Route path="/user-management" element={
              <ProtectedRoute>
                <UserManagement />
              </ProtectedRoute>
            } />

            {/* Personnel Qualifications Route */}
            <Route path="/personnel" element={
              <ProtectedRoute>
                <PersonnelDirectory />
              </ProtectedRoute>
            } />

            {/* Personnel Profile Route */}
            <Route path="/personnel/:userId" element={
              <ProtectedRoute>
                <PersonnelProfile />
              </ProtectedRoute>
            } />

            {/* Master SOP Register */}
            <Route path="/master-sop-register" element={
              <ProtectedRoute>
                <MasterSOPRegister />
              </ProtectedRoute>
            } />

            {/* Forms Library */}
            <Route path="/forms-library" element={
              <ProtectedRoute>
                <FormsLibrary />
              </ProtectedRoute>
            } />

            {/* Form Portal */}
            <Route path="/form-portal" element={
              <ProtectedRoute>
                <FormPortal />
              </ProtectedRoute>
            } />

            {/* Form Create Route */}
            <Route path="/forms/create/:schemaId" element={
              <ProtectedRoute>
                <FormRenderer mode="create" />
              </ProtectedRoute>
            } />

            {/* Form Edit Route */}
            <Route path="/forms/edit/:formId" element={
              <ProtectedRoute>
                <FormRenderer mode="edit" />
              </ProtectedRoute>
            } />

            {/* Form View Route */}
            <Route path="/forms/view/:formId" element={
              <ProtectedRoute>
                <FormRenderer mode="view" />
              </ProtectedRoute>
            } />

            {/* Knowledge Base */}
            <Route path="/knowledge" element={
              <ProtectedRoute>
                <KnowledgeBaseManager />
              </ProtectedRoute>
            } />

            {/* ILO App (AI Assistant) */}
            <Route path="/ilo" element={
              <ProtectedRoute>
                <ILOApp />
              </ProtectedRoute>
            } />

            {/* Default redirect */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
